import { ref } from 'vue';
import { exportToExcel, formatDateToPtBR, type ExportColumnDef } from '@/shared/lib';
import { useFormBuilderToast } from './useFormBuilderToast';
import { useFormBuilderStore } from '../model/form-builder.store';
import { useFormRulesStore } from '../model/form-rules.store';
import {
  buildReportFieldsExportRows,
  type ReportFieldExportRow,
} from '../lib/report-fields-export';

const REPORT_FIELDS_COLUMNS: ExportColumnDef<ReportFieldExportRow>[] = [
  { header: 'Identificador', width: 28, getValue: (row) => row.identifier },
  { header: 'Campo', width: 22, getValue: (row) => row.field },
  { header: 'Possui Regra', width: 14, getValue: (row) => row.hasRule },
  { header: 'Regra', width: 60, getValue: (row) => row.rule, wrapText: true },
  { header: 'Âncora', width: 12, getValue: (row) => row.anchor },
];

const SHEET_NAME = 'Campos para Relatório';
const FILE_NAME_PREFIX = 'relatorio-matrix';

function buildFileName(): string {
  return `${FILE_NAME_PREFIX}-${formatDateToPtBR(new Date()).replace(/\//g, '-')}.xlsx`;
}

export function useReportFieldsExport() {
  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();
  const toast = useFormBuilderToast();
  const isExporting = ref(false);

  async function exportReportFields() {
    if (isExporting.value) return;

    const rows = buildReportFieldsExportRows(
      formBuilderStore.elements,
      formRulesStore.rules,
      formRulesStore.reportFieldIds,
      formRulesStore.anchorFieldIds
    );

    if (rows.length === 0) {
      toast.showWarningToast({
        title: 'Nenhum campo para relatório configurado',
        message: 'Configure ao menos um campo para relatório antes de exportar.',
        type: 'warning',
      });
      return;
    }

    isExporting.value = true;

    const progressToastId = toast.showWarningToast(
      {
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      },
      {
        timeout: false,
        closeButton: false,
      }
    );

    try {
      await exportToExcel(rows, REPORT_FIELDS_COLUMNS, {
        sheetName: SHEET_NAME,
        fileName: buildFileName(),
      });

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message:
          rows.length === 1 ? '1 registro exportado' : `${rows.length} registros exportados`,
        type: 'success',
      });
    } catch {
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: 'Não foi possível exportar a lista de regras. Tente novamente.',
        type: 'error',
      });
    } finally {
      toast.dismissToast(progressToastId);
      isExporting.value = false;
    }
  }

  return {
    isExporting,
    exportReportFields,
  };
}
